import { uuidv7 } from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'
import { deviceId } from '../device.js'
import {
  META_BOOTSTRAPPED,
  META_CURSOR,
  META_LAST_SYNC,
  META_SHAPE,
  REPLICA_SHAPE,
  dbFor,
  getMeta,
  setMeta,
  type IobizDB,
  type LocalRow,
  type OutboxEntry,
} from '../db.js'

/**
 * Moteur de synchronisation (§7.6).
 *
 * Boucle : amorcer si nécessaire → tirer le delta → pousser la file d'attente.
 * Tout passe par la réplique locale ; le réseau n'est qu'un moyen de la tenir
 * à jour, jamais un préalable à l'affichage.
 */

const ENTITIES = [
  'customer',
  'item',
  'category',
  'supplier',
  'stock_level',
  'transaction',
  'tax',
  'appointment',
  'cash_session',
  'cash_movement',
  'blocage',
] as const
type Entity = (typeof ENTITIES)[number]

const TABLES: Record<
  Entity,
  | 'customers'
  | 'items'
  | 'categories'
  | 'suppliers'
  | 'stockLevels'
  | 'transactions'
  | 'taxes'
  | 'appointments'
  | 'cashSessions'
  | 'cashMovements'
  | 'blocages'
> = {
  customer: 'customers',
  item: 'items',
  category: 'categories',
  supplier: 'suppliers',
  stock_level: 'stockLevels',
  transaction: 'transactions',
  tax: 'taxes',
  appointment: 'appointments',
  cash_session: 'cashSessions',
  cash_movement: 'cashMovements',
  blocage: 'blocages',
}

const PAGE = 500
const MAX_ATTEMPTS = 8

export type SyncPhase = 'idle' | 'bootstrapping' | 'pulling' | 'pushing' | 'offline' | 'error'

export interface SyncStatus {
  phase: SyncPhase
  /** Progression de l'amorçage, 0 à 1. */
  progress: number
  pending: number
  conflicts: number
  /**
   * Mutations que le serveur a REFUSÉES et qui ne repartiront jamais.
   *
   * ⚠️ Distinct de `pending` : une mutation en attente finira par partir, une
   * mutation bloquée non. Les confondre laissait l'entête afficher
   * « Synchronisé » — donc « tout est enregistré sur le serveur » — pour une
   * saisie qui n'existait que sur l'appareil. C'est ce mensonge qui a rendu le
   * défaut invisible : l'article s'affichait, le serveur ne le connaissait pas,
   * et rien ne le signalait.
   */
  bloquees: number
  /** Motif de refus de la première mutation bloquée, tel que le serveur l'a dit. */
  motifBlocage: string | null
  lastSyncAt: string | null
  error: string | null
}

type Listener = (status: SyncStatus) => void

// --------------------------------------------------------------------- état

let status: SyncStatus = {
  /**
   * ⚠️ « bootstrapping », pas « idle ».
   *
   * L'état initial d'`idle` faisait afficher « Synchronisé » AVANT le moindre
   * échange avec le serveur : l'entête certifiait que tout était à jour alors
   * que la réplique pouvait être vide. On croyait une liste complète parce
   * qu'un badge vert le disait, et un écran qui dépend d'une entité pas encore
   * amorcée — les taux de TVA, par exemple — semblait simplement vide.
   */
  phase: 'bootstrapping',
  progress: 0,
  pending: 0,
  conflicts: 0,
  bloquees: 0,
  motifBlocage: null,
  lastSyncAt: null,
  error: null,
}

const listeners = new Set<Listener>()

function emit(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch }
  for (const l of listeners) l(status)
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  listener(status)
  return () => listeners.delete(listener)
}

export function currentStatus(): SyncStatus {
  return status
}

// ------------------------------------------------------------------ amorçage

/**
 * Chargement initial, entité par entité, page par page.
 *
 * Non bloquant : les clients arrivent d'abord et la liste devient utilisable
 * pendant que le catalogue se charge encore. Attendre la réplique complète
 * avant d'afficher quoi que ce soit ferait de la première ouverture une
 * attente de plusieurs dizaines de secondes sur un réseau lent.
 */
async function bootstrap(db: IobizDB, storeId: string): Promise<void> {
  emit({ phase: 'bootstrapping', progress: 0, error: null })

  let cursor = '0'
  let done = 0
  const echecs: string[] = []

  for (const [index, entity] of ENTITIES.entries()) {
    // ⚠️ Chaque entité est amorcée INDÉPENDAMMENT. En série sans filet, une
    // erreur sur les articles empêchait le chargement des catégories et des
    // fournisseurs, qui viennent après : l'utilisateur voyait un catalogue
    // sans aucune catégorie, sans le moindre message.
    try {
      let after: string | null = null
      do {
      const query = new URLSearchParams({ entity, limit: String(PAGE) })
      if (after) query.set('after', after)

      const page: {
        rows: LocalRow[]
        nextAfter: string | null
        cursor: string
        total: number
      } = await api.get(`/stores/${storeId}/sync/bootstrap?${query.toString()}`)

      // Le curseur le plus ANCIEN fait foi : repartir du plus récent
      // sauterait les changements survenus pendant l'amorçage des entités
      // précédentes. Rejouer est sans danger, perdre ne l'est pas.
      if (cursor === '0' || BigInt(page.cursor) < BigInt(cursor)) cursor = page.cursor

      if (page.rows.length > 0) {
        await db.table(TABLES[entity]).bulkPut(page.rows.map((r) => ({ ...r, _dirty: 0 as const })))
      }
        after = page.nextAfter
      } while (after)
    } catch (cause) {
      echecs.push(entity)
      // On continue : mieux vaut trois entités sur quatre qu'aucune.
      //
      // ⚠️ Mais on DIT laquelle a échoué et pourquoi. Le `catch` muet d'origine
      // rendait un amorçage bloqué indéchiffrable : la réplique restait
      // partielle, le badge tournait sans fin, et rien — ni écran, ni console —
      // ne nommait l'entité fautive.
      console.error(
        `[sync] amorçage impossible pour « ${entity} »`,
        cause instanceof Error ? cause.message : cause,
      )
    }

    done = index + 1
    emit({ progress: done / ENTITIES.length })
  }

  await setMeta(db, META_CURSOR, cursor)

  // ⚠️ L'amorçage n'est déclaré ABOUTI que si TOUTES les entités sont passées.
  // Sinon on laisse le drapeau à faux : le cycle suivant réessaiera, au lieu
  // de figer une réplique incomplète pour toujours.
  if (echecs.length === 0) {
    await setMeta(db, META_BOOTSTRAPPED, true)
    await setMeta(db, META_SHAPE, REPLICA_SHAPE)
  } else {
    emit({ error: `Chargement incomplet : ${echecs.join(', ')}. Nouvel essai au prochain cycle.` })
  }
}

// --------------------------------------------------------------------- delta

async function pull(db: IobizDB, storeId: string): Promise<void> {
  emit({ phase: 'pulling' })
  let cursor = await getMeta(db, META_CURSOR, '0')
  let hasMore = true

  while (hasMore) {
    let page: {
      changes: { entity: Entity; entityId: string; op: string; data: LocalRow | null }[]
      nextCursor: string
      hasMore: boolean
    }

    try {
      page = await api.get(
        `/stores/${storeId}/sync/changes?since=${cursor}&limit=${PAGE}&deviceId=${deviceId()}`,
      )
    } catch (error) {
      // 410 : le journal a été purgé au-delà de notre curseur. Le delta ne
      // peut plus nous rattraper, il faut tout reprendre.
      if (error instanceof ApiError && error.status === 410) {
        await setMeta(db, META_BOOTSTRAPPED, false)
        await bootstrap(db, storeId)
        return
      }
      throw error
    }

    let inapplicables = 0

    for (const change of page.changes) {
      const nomTable = TABLES[change.entity]
      // Entité que cette version du client ne connaît pas — un serveur plus
      // récent en diffuse une nouvelle, par exemple.
      if (!nomTable) {
        inapplicables += 1
        continue
      }

      try {
        const table = db.table(nomTable)
        if (change.op === 'delete' || !change.data) {
          await table.delete(change.entityId)
          continue
        }

        // ⚠️ Une ligne modifiée localement et pas encore confirmée n'est JAMAIS
        // écrasée par le serveur : la saisie en cours de l'utilisateur prime
        // jusqu'à ce que sa mutation ait été poussée et arbitrée.
        const local = (await table.get(change.entityId)) as LocalRow | undefined
        if (local?._dirty === 1) continue

        await table.put({ ...change.data, _dirty: 0 })
      } catch {
        inapplicables += 1
      }
    }

    cursor = page.nextCursor
    hasMore = page.hasMore
    await setMeta(db, META_CURSOR, cursor)

    // ⭐ Un changement qu'on n'a pas su appliquer est DÉFINITIVEMENT perdu si
    // on laisse simplement le curseur passer devant : le cycle suivant repart
    // après lui et ne le redemandera jamais. Silencieusement, l'appareil se
    // met à diverger du serveur — c'est exactement ce qui est arrivé aux
    // niveaux de stock, consommés au rang 16 sans être écrits.
    //
    // On redemande donc un amorçage complet, qui relit tout depuis la source.
    if (inapplicables > 0) {
      await setMeta(db, META_BOOTSTRAPPED, false)
      emit({ error: `${inapplicables} changement(s) non appliqué(s) — réamorçage demandé.` })
      return
    }
  }
}

// ---------------------------------------------------------- file d'attente

/** Ajoute une écriture à la file. Renvoie l'identifiant de mutation. */
export async function enqueue(
  storeId: string,
  entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'status' | 'nextAttemptAt'>,
): Promise<string> {
  const db = dbFor(storeId)
  const id = uuidv7()
  await db.outbox.add({
    ...entry,
    id,
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: 'pending',
    nextAttemptAt: 0,
  })
  await refreshCounters(db)
  return id
}

/** Reprise exponentielle : 1 s, 2 s, 4 s… plafonnée à 5 minutes (§7.6). */
function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** attempts, 5 * 60_000)
}

async function push(db: IobizDB, storeId: string): Promise<void> {
  const now = Date.now()
  const ready = await db.outbox
    .where('status')
    .anyOf('pending', 'failed')
    .filter((e) => e.nextAttemptAt <= now)
    .sortBy('createdAt')

  if (ready.length === 0) return
  emit({ phase: 'pushing' })

  // Le serveur plafonne les lots à 100 mutations.
  const batch = ready.slice(0, 100)
  await db.outbox.bulkUpdate(batch.map((e) => ({ key: e.id, changes: { status: 'sending' } })))

  let results: {
    results: {
      mutationId: string
      status: string
      version?: number
      serverVersion?: number
      serverState?: Record<string, unknown>
      conflictingFields?: string[]
      reason?: string
    }[]
  }

  try {
    results = await api.post(`/stores/${storeId}/sync/push`, {
      deviceId: deviceId(),
      mutations: batch.map((e) => ({
        id: e.id,
        entity: e.entity,
        entityId: e.entityId,
        op: e.op,
        baseVersion: e.baseVersion,
        payload: e.payload,
      })),
    })
  } catch (error) {
    // Échec RÉSEAU : rien n'a été appliqué, on réarme pour plus tard. Les
    // mutations restent en file — c'est tout l'objet de la file.
    await db.outbox.bulkUpdate(
      batch.map((e) => ({
        key: e.id,
        changes: {
          status: 'failed' as const,
          attempts: e.attempts + 1,
          nextAttemptAt: Date.now() + backoffMs(e.attempts),
          lastError: error instanceof Error ? error.message : 'Envoi impossible',
        },
      })),
    )
    throw error
  }

  for (const result of results.results) {
    const entry = batch.find((e) => e.id === result.mutationId)
    if (!entry) continue
    const table = db.table(TABLES[entry.entity])

    if (result.status === 'accepted' || result.status === 'merged' || result.status === 'duplicate') {
      await db.outbox.delete(entry.id)

      /**
       * ⭐ On adopte l'état RENVOYÉ par le serveur, pas seulement son numéro de
       * version.
       *
       * ⚠️ Le serveur enrichit ce qu'il reçoit : il attribue le numéro légal
       * d'une facture, recalcule un statut, normalise un libellé. Et le filtre
       * anti-écho lui interdit de renvoyer à un appareil le changement dont
       * celui-ci est l'auteur. Sans cette adoption, une vente poussée gardait
       * éternellement sa référence provisoire à l'écran — « L-2026-… » — alors
       * qu'elle portait « IN-2026-0008 » en base. Le delta ne l'aurait jamais
       * corrigée.
       */
      const local = (await table.get(entry.entityId)) as LocalRow | undefined
      if (result.serverState) {
        await table.put({ ...result.serverState, _dirty: 0, _localOnly: 0 } as LocalRow)
      } else if (local) {
        await table.put({
          ...local,
          _dirty: 0,
          _localOnly: 0,
          version: result.version ?? local.version,
        })
      }
      continue
    }

    if (result.status === 'conflict') {
      await db.conflicts.put({
        id: entry.id,
        entity: entry.entity,
        entityId: entry.entityId,
        fields: result.conflictingFields ?? [],
        localPayload: entry.payload,
        serverState: result.serverState ?? {},
        serverVersion: result.serverVersion ?? 0,
        detectedAt: new Date().toISOString(),
      })
      await db.outbox.update(entry.id, { status: 'conflict' })
      continue
    }

    // `rejected` : le serveur ne veut pas de cette écriture et ne la voudra
    // pas davantage au dixième essai. On la sort de la file et on la signale,
    // plutôt que de boucler indéfiniment.
    await db.outbox.update(entry.id, {
      status: 'failed',
      attempts: MAX_ATTEMPTS,
      lastError: result.reason ?? 'Refusée par le serveur',
      nextAttemptAt: Number.MAX_SAFE_INTEGER,
    })
  }
}

/**
 * Renvoie à l'assaut les mutations que le serveur avait refusées.
 *
 * ⭐ Une mutation refusée est parquée pour toujours — à juste titre quand le
 * refus tient à la saisie elle-même : réessayer cent fois ne la rendra pas
 * valide. Mais un refus peut aussi venir du SERVEUR, et cesser d'être vrai
 * après une correction : c'est arrivé avec le plafond de taille des visuels,
 * relevé dans le schéma applicatif et pas dans la base. Les mutations
 * concernées étaient alors condamnées, sans aucun moyen de les rejouer depuis
 * l'interface — la saisie de l'utilisateur était perdue pour de bon.
 *
 * Le compteur de tentatives est remis à zéro : c'est une NOUVELLE chance, pas
 * la suite de l'ancienne série.
 */
export async function relancerBloquees(storeId: string): Promise<number> {
  const db = dbFor(storeId)
  const bloquees = await db.outbox
    .where('status')
    .equals('failed')
    .filter((e) => e.nextAttemptAt === Number.MAX_SAFE_INTEGER)
    .toArray()

  if (bloquees.length === 0) return 0

  await db.outbox.bulkUpdate(
    bloquees.map((e) => ({
      key: e.id,
      changes: { status: 'pending' as const, attempts: 0, nextAttemptAt: 0, lastError: undefined },
    })),
  )
  await runCycle(storeId)
  return bloquees.length
}

async function refreshCounters(db: IobizDB): Promise<void> {
  const pending = await db.outbox.where('status').anyOf('pending', 'sending', 'failed').count()
  const conflicts = await db.conflicts.count()

  // Une mutation refusée est parquée avec `nextAttemptAt` à l'infini : c'est
  // exactement ce qui la distingue d'un envoi qui attend sa prochaine tentative.
  const bloquees = await db.outbox
    .where('status')
    .equals('failed')
    .filter((e) => e.nextAttemptAt === Number.MAX_SAFE_INTEGER)
    .toArray()

  emit({
    pending,
    conflicts,
    bloquees: bloquees.length,
    motifBlocage: bloquees[0]?.lastError ?? null,
  })
}

// ------------------------------------------------------------------- cycle

let running = false

/** Un cycle complet. Réentrance interdite : deux cycles concurrents se
 *  disputeraient le curseur et rejoueraient les mêmes mutations. */
export async function runCycle(storeId: string): Promise<void> {
  if (running) return
  running = true
  const db = dbFor(storeId)

  try {
    if (!navigator.onLine) {
      emit({ phase: 'offline' })
      await refreshCounters(db)
      return
    }

    // La forme projetée a changé depuis le dernier amorçage : les lignes
    // existantes ne recevront jamais les colonnes ajoutées, puisqu'elles n'ont
    // pas bougé. On refait donc un amorçage complet.
    //
    // ⚠️ SANS vider les tables au préalable. L'amorçage réécrit chaque ligne
    // (bulkPut) : vider d'abord ne servirait qu'à laisser l'utilisateur devant
    // une réplique VIDE si le réseau tombe entre les deux — c'est-à-dire à
    // détruire ses données au moment précis où il en a le plus besoin.
    const forme = await getMeta(db, META_SHAPE, 0)
    const formeObsolete = forme !== REPLICA_SHAPE

    if (formeObsolete || !(await getMeta(db, META_BOOTSTRAPPED, false))) {
      await bootstrap(db, storeId)
    }

    await pull(db, storeId)
    await push(db, storeId)
    await refreshCounters(db)

    const now = new Date().toISOString()
    await setMeta(db, META_LAST_SYNC, now)
    emit({ phase: 'idle', progress: 1, lastSyncAt: now, error: null })
  } catch (error) {
    emit({
      phase: navigator.onLine ? 'error' : 'offline',
      error: error instanceof Error ? error.message : 'Synchronisation impossible',
    })
    await refreshCounters(db)
  } finally {
    running = false
  }
}

let timer: number | null = null
let currentStore: string | null = null

/** Démarre la boucle pour une boutique et la maintient jusqu'à `stop()`. */
/**
 * Battement de la boucle, en millisecondes.
 *
 * ⭐ Surchargeable par `localStorage['iobiz.syncIntervalMs']`. C'est ce qui
 * rend les tests de bout en bout tenables : sans cela, chaque scénario hors
 * ligne attend un cycle de 30 s d'horloge réelle, et finit par dépasser le
 * plafond de temps d'un test dès que la machine est un peu lente — c'est ce
 * qui les a fait échouer à leur première exécution en CI.
 *
 * Le pire qu'un utilisateur puisse en faire est de synchroniser plus ou moins
 * souvent : aucune règle métier n'en dépend.
 */
function battement(defaut: number): number {
  const brut = Number(localStorage.getItem('iobiz.syncIntervalMs'))
  return Number.isFinite(brut) && brut >= 200 ? brut : defaut
}

export function start(storeId: string, intervalMs = 30_000): void {
  if (currentStore === storeId && timer !== null) return
  stop()
  currentStore = storeId

  void runCycle(storeId)
  timer = window.setInterval(() => void runCycle(storeId), battement(intervalMs))

  // Le retour du réseau ne doit pas attendre le prochain battement : c'est
  // exactement le moment où l'utilisateur veut voir sa file se vider.
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
}

function onOnline(): void {
  if (currentStore) void runCycle(currentStore)
}

function onOffline(): void {
  emit({ phase: 'offline' })
}

export function stop(): void {
  if (timer !== null) window.clearInterval(timer)
  timer = null
  currentStore = null
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
}
